// src/BlitzHowTo/tutorialContext.jsx
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { tutorialStorage } from './tutorialStorage';
import { getTutorialForRoute } from './tutorialRegistry';

const TutorialContext = createContext();

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return context;
};

export const TutorialProvider = ({ children, userIdentifier }) => {
  const location = useLocation();
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // ═══════════════════════════════════════════════════════════
  // MOBILE DETECTION - Disable tutorials on mobile for now
  // ═══════════════════════════════════════════════════════════
  const isMobile = useMediaQuery('(max-width:960px)');
  
  // Refs for tracking state
  const attemptedRoutes = useRef(new Set());
  const lastUserIdentifier = useRef(null);
  const lastPathname = useRef(null);
  const initTimer = useRef(null);
  const retryTimer = useRef(null);
  const pendingTutorial = useRef(null);
  const retryCount = useRef(0);
  const maxRetries = 5;
  
  // Log mobile status changes
  useEffect(() => {
    console.log('[Tutorial] 📱 Mobile detection:', isMobile ? 'MOBILE (tutorials disabled)' : 'DESKTOP');
  }, [isMobile]);
  
  // ═══════════════════════════════════════════════════════════
  // FALLBACK: Try to get userIdentifier from localStorage
  // ═══════════════════════════════════════════════════════════
  const getFallbackUserIdentifier = useCallback(() => {
    try {
      // Try to get from stored auth data
      const storedUserId = localStorage.getItem('blitz_current_user_id');
      if (storedUserId) {
        console.log('[Tutorial] 🔄 Using fallback user ID from localStorage:', storedUserId);
        return `user_${storedUserId}`;
      }

      // Try to get from auth token
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        try {
          const payload = JSON.parse(atob(authToken.split('.')[1]));
          const userId = payload.user_id || payload.sub || payload.id;
          if (userId) {
            console.log('[Tutorial] 🔄 Using fallback user ID from token:', userId);
            // Store it for next time
            localStorage.setItem('blitz_current_user_id', userId);
            return `user_${userId}`;
          }
        } catch (e) {
          console.warn('[Tutorial] Failed to decode auth token:', e);
        }
      }

      // Try creator token
      const creatorToken = localStorage.getItem('creatorToken');
      if (creatorToken) {
        try {
          const payload = JSON.parse(atob(creatorToken.split('.')[1]));
          const username = payload.username || payload.creator_username;
          if (username) {
            console.log('[Tutorial] 🔄 Using fallback creator username:', username);
            return `creator_${username}`;
          }
        } catch (e) {
          console.warn('[Tutorial] Failed to decode creator token:', e);
        }
      }
    } catch (error) {
      console.warn('[Tutorial] Error in fallback identifier lookup:', error);
    }
    
    return null;
  }, []);

  // ═══════════════════════════════════════════════════════════
  // EFFECTIVE USER IDENTIFIER: Use prop or fallback
  // ═══════════════════════════════════════════════════════════
  const effectiveUserIdentifier = userIdentifier || getFallbackUserIdentifier();

  // ═══════════════════════════════════════════════════════════
  // INITIALIZATION: Wait for userIdentifier with retry logic
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    console.log('[Tutorial] 🔍 Checking auth state:', {
      propUserIdentifier: userIdentifier,
      effectiveUserIdentifier,
      isInitialized,
      retryCount: retryCount.current,
      isMobile
    });
    
    // Clear any pending timers
    if (initTimer.current) {
      clearTimeout(initTimer.current);
      initTimer.current = null;
    }
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }

    // Case 1: We have a user identifier - initialize!
    if (effectiveUserIdentifier && !isInitialized) {
      console.log('[Tutorial] ✅ User authenticated, initializing...', effectiveUserIdentifier);
      
      initTimer.current = setTimeout(() => {
        setIsInitialized(true);
        retryCount.current = 0;
        console.log('[Tutorial] 🎉 Initialization complete!');
        
        // Process pending tutorial immediately (but check mobile first)
        if (pendingTutorial.current && !isMobile) {
          console.log('[Tutorial] 📋 Processing pending tutorial:', pendingTutorial.current.title);
          const pending = pendingTutorial.current;
          pendingTutorial.current = null;
          
          setTimeout(() => {
            tryShowTutorial(pending, effectiveUserIdentifier);
          }, 500);
        } else if (pendingTutorial.current && isMobile) {
          console.log('[Tutorial] 📱 Skipping pending tutorial on mobile device');
          pendingTutorial.current = null;
        }
      }, 300);
      
      return () => {
        if (initTimer.current) {
          clearTimeout(initTimer.current);
        }
      };
    }
    
    // Case 2: No user identifier but we're logged in - retry
    if (!effectiveUserIdentifier && !isInitialized) {
      const hasAuthToken = localStorage.getItem('authToken') || localStorage.getItem('creatorToken');
      
      if (hasAuthToken && retryCount.current < maxRetries) {
        retryCount.current++;
        console.log(`[Tutorial] ⏳ No user identifier yet, retrying (${retryCount.current}/${maxRetries})...`);
        
        retryTimer.current = setTimeout(() => {
          setIsInitialized(false);
        }, 1000 * retryCount.current);
        
        return () => {
          if (retryTimer.current) {
            clearTimeout(retryTimer.current);
          }
        };
      } else if (retryCount.current >= maxRetries) {
        console.warn('[Tutorial] ⚠️ Max retries reached, user identifier not available');
        retryCount.current = 0;
      }
    }
    
    // Case 3: User logged out
    if (!effectiveUserIdentifier && isInitialized) {
      console.log('[Tutorial] 👋 User logged out, resetting');
      setIsInitialized(false);
      attemptedRoutes.current.clear();
      pendingTutorial.current = null;
      retryCount.current = 0;
    }
    
    // Case 4: User changed
    if (effectiveUserIdentifier && effectiveUserIdentifier !== lastUserIdentifier.current) {
      console.log('[Tutorial] 🔄 User changed:', {
        from: lastUserIdentifier.current,
        to: effectiveUserIdentifier
      });
      
      attemptedRoutes.current.clear();
      lastUserIdentifier.current = effectiveUserIdentifier;
      
      if (!isInitialized) {
        setIsInitialized(false);
      }
    }
  }, [effectiveUserIdentifier, isInitialized, getFallbackUserIdentifier, userIdentifier, isMobile]);

  // ═══════════════════════════════════════════════════════════
  // HELPER: Try to show tutorial with safety checks
  // ═══════════════════════════════════════════════════════════
  const tryShowTutorial = useCallback((tutorial, userId) => {
    // ═══ MOBILE CHECK - Don't auto-show on mobile ═══
    if (isMobile) {
      console.log('[Tutorial] 📱 Skipping tutorial on mobile device');
      return false;
    }

    if (!tutorial || !userId) {
      console.log('[Tutorial] ❌ Cannot show tutorial - missing data:', { tutorial: !!tutorial, userId });
      return false;
    }

    const hasSeen = tutorialStorage.hasSeen(tutorial.id, userId);
    const hasSkipped = tutorialStorage.hasSkipped(tutorial.id, userId);

    console.log('[Tutorial] 📊 Tutorial status check:', {
      tutorialId: tutorial.id,
      title: tutorial.title,
      userId,
      hasSeen,
      hasSkipped,
      isMobile
    });

    if (!hasSeen && !hasSkipped) {
      console.log('[Tutorial] 🎓 AUTO-SHOWING tutorial:', tutorial.title);
      startTutorial(tutorial);
      return true;
    } else {
      console.log('[Tutorial] ⏭️ Tutorial already seen/skipped');
      return false;
    }
  }, [isMobile]);

  // ═══════════════════════════════════════════════════════════
  // AUTO-SHOW TUTORIAL: Main logic with queueing
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    const routeChanged = lastPathname.current !== location.pathname;
    if (routeChanged) {
      console.log('[Tutorial] 🧭 Route changed to:', location.pathname);
      lastPathname.current = location.pathname;
    }

    // ═══ EARLY EXIT FOR MOBILE ═══
    if (isMobile) {
      return;
    }

    const tutorial = getTutorialForRoute(location.pathname);
    if (!tutorial) {
      console.log('[Tutorial] ℹ️ No tutorial for route:', location.pathname);
      return;
    }

    const routeKey = `${location.pathname}_${tutorial.id}`;
    
    // Only check attemptedRoutes if initialized
    if (isInitialized && attemptedRoutes.current.has(routeKey)) {
      console.log('[Tutorial] ⏭️ Already attempted this tutorial in current session');
      return;
    }

    // Case 1: We're initialized and ready - show it!
    if (isInitialized && effectiveUserIdentifier) {
      // NOW mark it as attempted (not before)
      attemptedRoutes.current.add(routeKey);
      
      const showTimer = setTimeout(() => {
        tryShowTutorial(tutorial, effectiveUserIdentifier);
      }, 1500);
      
      return () => clearTimeout(showTimer);
    }
    
    // Case 2: Not initialized yet - queue it WITHOUT marking as attempted
    if (!isInitialized && tutorial) {
      console.log('[Tutorial] 📋 Queueing tutorial for when user auth is ready:', tutorial.title);
      pendingTutorial.current = tutorial;
    }
  }, [location.pathname, isInitialized, effectiveUserIdentifier, tryShowTutorial, isMobile]);

  // ═══════════════════════════════════════════════════════════
  // PUBLIC API METHODS
  // ═══════════════════════════════════════════════════════════
  const startTutorial = useCallback((tutorial) => {
    console.log('[Tutorial] ▶️ Starting:', tutorial.title);
    setActiveTutorial(tutorial);
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (!activeTutorial) return;
    
    if (currentStep < activeTutorial.steps.length - 1) {
      console.log('[Tutorial] ⏭️ Next step:', currentStep + 1, '/', activeTutorial.steps.length);
      setCurrentStep(prev => prev + 1);
    } else {
      console.log('[Tutorial] ✅ Tutorial complete');
      completeTutorial();
    }
  }, [activeTutorial, currentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      console.log('[Tutorial] ⏮️ Previous step:', currentStep - 1);
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const skipTutorial = useCallback(() => {
    console.log('[Tutorial] ⏩ Skipping tutorial');
    if (activeTutorial && effectiveUserIdentifier) {
      tutorialStorage.markAsSkipped(activeTutorial.id, effectiveUserIdentifier);
    }
    closeTutorial();
  }, [activeTutorial, effectiveUserIdentifier]);

  const completeTutorial = useCallback(() => {
    console.log('[Tutorial] 🎉 Completing tutorial');
    if (activeTutorial && effectiveUserIdentifier) {
      tutorialStorage.markAsSeen(activeTutorial.id, effectiveUserIdentifier);
    }
    closeTutorial();
  }, [activeTutorial, effectiveUserIdentifier]);

  const closeTutorial = useCallback(() => {
    console.log('[Tutorial] 🚪 Closing tutorial');
    setIsActive(false);
    setTimeout(() => {
      setActiveTutorial(null);
      setCurrentStep(0);
    }, 300);
  }, []);

  const resetTutorial = useCallback((tutorialId) => {
    console.log('[Tutorial] 🔄 Resetting tutorial:', tutorialId);
    if (effectiveUserIdentifier) {
      tutorialStorage.reset(tutorialId, effectiveUserIdentifier);
      attemptedRoutes.current.clear();
      
      // Don't auto-show on mobile even after reset
      if (!isMobile) {
        const currentTutorial = getTutorialForRoute(location.pathname);
        if (currentTutorial && (!tutorialId || currentTutorial.id === tutorialId)) {
          setTimeout(() => {
            tryShowTutorial(currentTutorial, effectiveUserIdentifier);
          }, 500);
        }
      }
    }
  }, [effectiveUserIdentifier, location.pathname, tryShowTutorial, isMobile]);

  // ═══════════════════════════════════════════════════════════
  // CLEANUP: Clear timers on unmount
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    return () => {
      if (initTimer.current) clearTimeout(initTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      console.log('[Tutorial] 🧹 Cleanup: cleared all timers');
    };
  }, []);

  const value = {
    activeTutorial,
    currentStep,
    isActive,
    isInitialized,
    isMobile,
    userIdentifier: effectiveUserIdentifier,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    _debug: {
      attemptedRoutes: Array.from(attemptedRoutes.current),
      pendingTutorial: pendingTutorial.current?.title || null,
      retryCount: retryCount.current,
      isMobile,
    },
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};